import { z } from "zod";
import { jsonColumn, SpotifyIdSchema } from "./common.ts";

/** Centre entity for an ego graph — ring is always artists. */
export const GraphCentreTypeSchema = z.enum(["artist", "album", "track"]);
export type GraphCentreType = z.infer<typeof GraphCentreTypeSchema>;

export const GraphEdgeSchema = z.object({
  neighbour_id: SpotifyIdSchema,
  via_track_ids: z.array(z.string()),
  via_album_ids: z.array(z.string()),
  shared_count: z.number().int().nonnegative(),
  weight: z.number().nonnegative(),
  track_shared: z.number().int().nonnegative(),
  album_shared: z.number().int().nonnegative(),
});
export type GraphEdge = z.infer<typeof GraphEdgeSchema>;

export const GraphNodeSchema = z.object({
  id: SpotifyIdSchema,
  name: z.string().nullable(),
  image_url: z.string().nullable(),
  track_count: z.number().int().nonnegative(),
  /** Dominant genre label for colouring; null when unknown. */
  genre_id: z.string().nullable().optional(),
  genre_name: z.string().nullable().optional(),
  genre_color: z.string().nullable().optional(),
  /** `user` | `spotify` — which source supplied the dominant genre. */
  genre_source: z.enum(["user", "spotify"]).nullable().optional(),
});
export type GraphNode = z.infer<typeof GraphNodeSchema>;

export const GraphRingMemberSchema = GraphNodeSchema.extend({
  edge: GraphEdgeSchema,
});
export type GraphRingMember = z.infer<typeof GraphRingMemberSchema>;

/** Depth-2 preview — small, unlabelled until hover. */
export const GraphGhostSchema = z.object({
  id: SpotifyIdSchema,
  name: z.string().nullable(),
  image_url: z.string().nullable(),
  parent_id: SpotifyIdSchema,
  weight: z.number().nonnegative(),
});
export type GraphGhost = z.infer<typeof GraphGhostSchema>;

export const GraphCentreSchema = z.object({
  type: GraphCentreTypeSchema,
  id: z.string(),
  name: z.string().nullable(),
  image_url: z.string().nullable(),
  track_count: z.number().int().nonnegative().optional(),
});
export type GraphCentre = z.infer<typeof GraphCentreSchema>;

export const GraphEgoSchema = z.object({
  centre: GraphCentreSchema,
  ring: z.array(GraphRingMemberSchema),
  total_neighbours: z.number().int().nonnegative(),
  ghosts: z.array(GraphGhostSchema),
  genre_source_in_play: z.enum(["user", "spotify", "mixed", "none"]),
  cluster: z
    .object({
      fully_connected: z.boolean(),
      artist_count: z.number().int().nonnegative(),
      shared_track_count: z.number().int().nonnegative(),
    })
    .nullable(),
});
export type GraphEgo = z.infer<typeof GraphEgoSchema>;

/** DB row shape for artist_edges. */
export const ArtistEdgeRowSchema = z.object({
  artist_a: SpotifyIdSchema,
  artist_b: SpotifyIdSchema,
  via_track_ids: jsonColumn(z.array(z.string())),
  via_album_ids: jsonColumn(z.array(z.string())),
  shared_count: z.number().int(),
  weight: z.number(),
  rebuilt_at: z.string(),
});
export type ArtistEdgeRow = z.infer<typeof ArtistEdgeRowSchema>;

export const GraphSharedItemsSchema = z.object({
  tracks: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      cover_url: z.string().nullable(),
      duration_ms: z.number().int().nullable(),
      album_name: z.string().nullable().optional(),
      artist_names: z.array(z.string()),
    }),
  ),
  albums: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      cover_url: z.string().nullable(),
      release_date: z.string().nullable(),
    }),
  ),
});
export type GraphSharedItems = z.infer<typeof GraphSharedItemsSchema>;

/** Human edge label fragments — count stays numeric; kind text is separate. */
export function edgeKindLabel(trackShared: number, albumShared: number): string {
  if (trackShared > 0 && albumShared > 0) {
    return `${trackShared} track${trackShared === 1 ? "" : "s"}, ${albumShared} album${albumShared === 1 ? "" : "s"}`;
  }
  if (trackShared === 1 && albumShared === 0) return "via track";
  if (trackShared > 1 && albumShared === 0) {
    return `${trackShared} tracks`;
  }
  if (albumShared === 1 && trackShared === 0) return "via album";
  if (albumShared > 1 && trackShared === 0) {
    return `${albumShared} albums`;
  }
  return "linked";
}
